import { ILocationClosureInterval } from '~/client/graph'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import IMsDateInterval from '~/client/src/shared/types/IMsDateInterval'
import {
  isWeekdayNumber,
  resolveOperatingIntervalIsDaily,
} from '~/client/src/shared/utils/operatingIntervalMode'
import { withSingleDailyInterval } from '~/client/src/shared/utils/operatingIntervals'

// Whether an operating window can still apply to a booking: a daily one always
// can; a dated one only through the end of its last day. Mirrors what
// getLocationActiveClosures keeps for the Date closures column.
export function isOperatingIntervalCurrentFmt(
  projectDateStore: ProjectDateStore,
  interval: IMsDateInterval,
): boolean {
  if (resolveOperatingIntervalIsDaily(interval)) return true

  const { endDate, startDate } = interval
  return projectDateStore.endOfDay(endDate ?? startDate).getTime() >= Date.now()
}

/** How far ahead a search for the next opening will look before giving up. */
const MAX_SEARCHED_DAYS = 14

/**
 * The ceiling on a caller-supplied search bound, so a mis-set date filter cannot
 * spin the day loop. A year and a day is past any range a picker offers.
 */
const MAX_BOUNDED_SEARCH_DAYS = 366

const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000

/** The interval that governs `date`, ignoring any tail reaching in from before it. */
function windowStartingOnDate(
  projectDateStore: ProjectDateStore,
  intervals: readonly IMsDateInterval[],
  date: Date | number,
): IMsDateInterval | undefined {
  const dayStart = projectDateStore.startOfDay(date).getTime()
  const dayEnd = projectDateStore.endOfDay(date).getTime()

  // Every day a dated window touches belongs to it, end-exclusive so one
  // finishing at midnight stops at the day before. Same days the validator's
  // buildConcreteOperatingWindows marks dated, and the two have to agree on the
  // set or the card publishes hours the booking form will refuse.
  const governing = intervals.filter(interval => {
    if (resolveOperatingIntervalIsDaily(interval)) return false

    const opensOn = projectDateStore.startOfDay(interval.startDate).getTime()
    const lastDay = projectDateStore
      .startOfDay((interval.endDate ?? interval.startDate) - 1)
      .getTime()

    return opensOn <= dayStart && lastDay >= dayStart
  })

  if (governing.length) {
    // Reportable as the day's own hours only where it opens on this day or
    // covers it end to end. A window that merely finishes here — a tail into the
    // morning, or the last hours of a multi-day override — is over, and offering
    // it as today's hours advertises one that has already closed.
    //
    // Returning nothing rather than falling through: the day is still governed,
    // so its recurring hours do not come back for the rest of it. They used to,
    // and the card published a normal shift on a day the validator had already
    // handed to the override.
    const reportable = governing.filter(interval => {
      const opensOn = projectDateStore.startOfDay(interval.startDate).getTime()
      const closesOn = projectDateStore
        .endOfDay(interval.endDate ?? interval.startDate)
        .getTime()

      return opensOn === dayStart || closesOn > dayEnd
    })

    return reportable.length ? reportable[reportable.length - 1] : undefined
  }

  const weekdayScoped = intervals.filter(interval =>
    isWeekdayNumber(interval.dayOfWeek),
  )
  if (weekdayScoped.length) {
    // A week supersedes any every-day window, as it does everywhere else, so a
    // weekday it omits is closed rather than falling through to one.
    const dayOfWeek = Number(projectDateStore.getWeekdayNumber(date))
    return weekdayScoped.find(interval => interval.dayOfWeek === dayOfWeek)
  }

  return intervals.filter(resolveOperatingIntervalIsDaily).pop()
}

/** A dated window's own absolute range, or undefined when it is not dated. */
function datedRange(
  interval: IMsDateInterval,
): { start: number; end: number } | undefined {
  if (resolveOperatingIntervalIsDaily(interval)) return undefined

  const start = interval.startDate
  const end = interval.endDate ?? interval.startDate

  return end > start ? { start, end } : undefined
}

/** Whether the window runs past midnight into the following day. */
function endsNextDay(
  projectDateStore: ProjectDateStore,
  interval: IMsDateInterval,
): boolean {
  const end = interval.endDate ?? interval.startDate
  return (
    projectDateStore.getDaySeconds(end) <=
    projectDateStore.getDaySeconds(interval.startDate)
  )
}

/**
 * The project's own week, as one window per weekday it works.
 *
 * What a resource stating no hours of its own is judged by: the booking
 * validator falls back to this, the controls chip advertises it, and the
 * jobsite homepage has to report the same thing or the three disagree about
 * whether a resource is open.
 */
export function buildProjectWeekWindowsFmt(
  projectDateStore: ProjectDateStore,
): IMsDateInterval[] {
  return projectDateStore.workingWeekdayNumbers.map(dayOfWeek => {
    const { startTime, endTime } =
      projectDateStore.getWorkingHoursForWeekday(dayOfWeek)

    return {
      startDate: Number(startTime),
      endDate: Number(endTime),
      isDaily: true,
      dayOfWeek,
    }
  })
}

/**
 * The window in effect at a given instant, or undefined when the resource works
 * none then.
 *
 * The same precedence the booking validator applies — a dated window governs its
 * own date; below that the weekday's own window decides; and a window carrying
 * no weekday applies to every day — plus the tail of the day before. A Monday
 * 20:00-04:00 window is still in effect at 02:00 on Tuesday, and reporting
 * Tuesday shut then would contradict the open/closed state, which the engine
 * computes from the same geometry.
 */
/**
 * The windows a resource is actually judged by, project hours included.
 *
 * One rule, shared: inheritance is decided on the RECURRING layer, the way the
 * booking validator decides it. A resource stating only a dated override has
 * spoken for one date and nothing about the rest of the week, so it still
 * inherits; the dated windows ride along and take precedence on the days they
 * cover. Superseded windows are collapsed first, since no migration enforces the
 * one-window-per-day rule on stored records.
 *
 * Shared so the surfaces answering "what is open, and when" cannot drift apart -
 * the jobsite homepage and the map card read the same list.
 */
export function buildEffectiveOperatingIntervalsFmt(
  projectDateStore: ProjectDateStore,
  intervals: readonly IMsDateInterval[] = [],
): IMsDateInterval[] {
  const own = withSingleDailyInterval([...intervals])

  return own.some(resolveOperatingIntervalIsDaily)
    ? own
    : [...own, ...buildProjectWeekWindowsFmt(projectDateStore)]
}

/**
 * The connected stretches a day's ranges cover, ones that meet joined into one.
 *
 * The booking engine reads its windows the same way: it walks them in order,
 * extends a cursor to each end, and only refuses when the next one starts after
 * it. Two windows that overlap or merely meet are therefore one stretch of open
 * time to it, and have to be one here too.
 */
function mergeConnectedRanges(
  ranges: readonly IConcreteRange[],
): Array<{ start: number; end: number }> {
  const merged: Array<{ start: number; end: number }> = []

  for (const range of [...ranges].sort((a, b) => a.start - b.start)) {
    const last = merged[merged.length - 1]

    if (last && range.start <= last.end) {
      last.end = Math.max(last.end, range.end)
      continue
    }

    merged.push({ start: range.start, end: range.end })
  }

  return merged
}

/**
 * The window in effect at a given instant, or undefined when the resource works
 * none then.
 *
 * The same precedence the booking validator applies — a dated window governs its
 * own date; below that the weekday's own window decides; and a window carrying
 * no weekday applies to every day — plus the tail of the day before. A Monday
 * 20:00-04:00 window is still in effect at 02:00 on Tuesday, and reporting
 * Tuesday shut then would contradict the open/closed state, which the engine
 * computes from the same geometry.
 *
 * Where windows overlap, what is reported is the stretch they jointly cover, not
 * whichever one is picked out of them. A Monday 18:00-05:00 shift running under
 * a dated Tuesday 00:00-06:00 leaves the resource open from 18:00 to 06:00
 * continuously; naming either source window alone gets one end of that right and
 * the other wrong, and the engine — which unions them — would accept a booking
 * the card said was outside the hours.
 */
export function resolveOperatingWindowForDateFmt(
  projectDateStore: ProjectDateStore,
  intervals: readonly IMsDateInterval[],
  date: Date | number,
): IMsDateInterval | undefined {
  const at = new Date(date).getTime()

  const covering = mergeConnectedRanges(
    openRangesForDate(projectDateStore, intervals, new Date(date)),
  ).find(span => span.start <= at && at < span.end)

  // A computed stretch rather than a stored interval: it is read for its two
  // instants, which is all a reported window is.
  if (covering) {
    return { startDate: covering.start, endDate: covering.end }
  }

  // Nothing running: the day's own window, which the card still reports once it
  // has passed or before it begins.
  return windowStartingOnDate(projectDateStore, intervals, date)
}

export function findNextOpeningFmt(
  projectDateStore: ProjectDateStore,
  intervals: readonly IMsDateInterval[],
  from: Date | number,
  closures: readonly IMsDateInterval[] = [],
  searchUntil?: Date | number,
): { startDate: number; interval: IMsDateInterval } | undefined {
  const { startOfDay, addDays } = projectDateStore
  const fromMs = new Date(from).getTime()

  // MAX_SEARCHED_DAYS is a giving-up point for the open question "when does this
  // open again", where there is no natural stop. A caller asking the bounded
  // question - "does it open before this date" - carries its own, so it says so
  // and the day loop follows the period rather than a constant chosen for a
  // different question. Shorter periods therefore search LESS than the default,
  // not more.
  const lastOffset =
    searchUntil == null
      ? MAX_SEARCHED_DAYS
      : Math.min(
          Math.max(
            Math.round(
              (startOfDay(searchUntil).getTime() - startOfDay(from).getTime()) /
                MILLISECONDS_IN_DAY,
            ),
            0,
          ),
          MAX_BOUNDED_SEARCH_DAYS,
        )

  for (let offset = 0; offset <= lastOffset; offset++) {
    const day = addDays(startOfDay(from), offset)

    for (const range of openRangesForDate(projectDateStore, intervals, day)) {
      if (range.end <= fromMs) continue

      const opening = firstOpenInstant(
        projectDateStore,
        Math.max(range.start, fromMs + 1),
        range.end,
        closures,
      )

      if (opening != null)
        return { startDate: opening, interval: range.interval }
    }
  }

  return undefined
}

/** A window placed on the calendar: the absolute stretch it covers. */
interface IConcreteRange {
  start: number
  end: number
  interval: IMsDateInterval
}

/** Puts a window on a given day, carrying its end into the next where it wraps. */
function placeOnDay(
  projectDateStore: ProjectDateStore,
  interval: IMsDateInterval,
  day: Date,
): IConcreteRange {
  const { getHoursMinutes, setHours, addDays } = projectDateStore
  const [openHours, openMinutes] = getHoursMinutes(interval.startDate)
  const [closeHours, closeMinutes] = getHoursMinutes(
    interval.endDate ?? interval.startDate,
  )

  const start = setHours(new Date(day), openHours, openMinutes).getTime()
  let end = setHours(new Date(day), closeHours, closeMinutes).getTime()
  if (end <= start) end = addDays(new Date(end), 1).getTime()

  return { start, end, interval }
}

/**
 * Every stretch a resource is open across a date, earliest first.
 *
 * Two of them at most: the tail of a window that began the day before and runs
 * past midnight, and the day's own window. Missing the first would step over a
 * reopening inside a window already running — a night shift interrupted by a
 * closure reopens within that shift, not on some later day.
 */
function openRangesForDate(
  projectDateStore: ProjectDateStore,
  intervals: readonly IMsDateInterval[],
  day: Date,
): IConcreteRange[] {
  const { startOfDay, addDays } = projectDateStore
  const ranges: IConcreteRange[] = []

  // Dated windows keep their own range. placeOnDay rebuilds a window from its
  // clock times onto one day, which collapses a multi-day override into its
  // first day and loses the rest - including the final-day hours a closure
  // search has to see to name the real reopening.
  const dayStart = startOfDay(day).getTime()
  const dayEnd = projectDateStore.endOfDay(day).getTime()
  for (const interval of intervals) {
    const range = datedRange(interval)
    if (range && range.start <= dayEnd && range.end > dayStart) {
      ranges.push({ ...range, interval })
    }
  }

  const previousDay = addDays(startOfDay(day), -1)
  const carried = windowStartingOnDate(projectDateStore, intervals, previousDay)
  if (
    carried &&
    !datedRange(carried) &&
    endsNextDay(projectDateStore, carried)
  ) {
    ranges.push(placeOnDay(projectDateStore, carried, previousDay))
  }

  const own = windowStartingOnDate(projectDateStore, intervals, day)
  if (own && !datedRange(own))
    ranges.push(placeOnDay(projectDateStore, own, day))

  return ranges.sort((a, b) => a.start - b.start)
}

/**
 * The first instant in [from, until) that no closure covers, or undefined when
 * closures swallow the stretch.
 *
 * A window the site is shut through is not an opening: reporting one would name
 * a time the location still cannot be used, which is the difference between the
 * hours it keeps and the hours it is actually available.
 */
function firstOpenInstant(
  projectDateStore: ProjectDateStore,
  from: number,
  until: number,
  closures: readonly IMsDateInterval[],
): number | undefined {
  let candidate = from

  // Each push can land inside another closure, so this repeats until the
  // candidate survives a full pass.
  let moved = true
  while (moved && candidate < until) {
    moved = false

    for (const closure of closures) {
      const reopensAt = closureEndCovering(projectDateStore, closure, candidate)

      if (reopensAt != null) {
        candidate = reopensAt
        moved = true
      }
    }
  }

  return candidate < until ? candidate : undefined
}

/**
 * When the closure covering an instant lifts, or undefined when none does.
 *
 * A daily closure recurs, so its stored dates are time-of-day carriers and it is
 * placed on the day in question — and on the day before, for one that runs
 * through midnight. Comparing its raw timestamps instead would let every day but
 * its anchor date pass as open, which is the same reading `isClosedBetween`
 * avoids through `isDailyIntervalIntersectsWithDates`.
 */
function closureEndCovering(
  projectDateStore: ProjectDateStore,
  closure: IMsDateInterval,
  at: number,
): number | undefined {
  const covers = (range: IConcreteRange): number | undefined =>
    range.start <= at && at < range.end ? range.end : undefined

  if (closure.isDaily !== true) {
    const end = closure.endDate ?? closure.startDate
    return closure.startDate <= at && at < end ? end : undefined
  }

  const { startOfDay, addDays } = projectDateStore
  const today = startOfDay(at)

  return (
    covers(placeOnDay(projectDateStore, closure, addDays(today, -1))) ??
    covers(placeOnDay(projectDateStore, closure, today))
  )
}

export function formatOperatingIntervalFmt(
  projectDateStore: ProjectDateStore,
  interval: IMsDateInterval,
): string {
  const { startDate, endDate, dayOfWeek } = interval

  // A window scoped to one weekday is named by that day. Calling it "Daily"
  // would claim the whole week, which is the one thing it does not mean.
  if (isWeekdayNumber(dayOfWeek)) {
    const dayName = projectDateStore.weekdayNamesList[dayOfWeek - 1]
    const timeOf = (date: number) => projectDateStore.getTimeToDisplay(date)

    return `${dayName}, ${timeOf(startDate)} – ${timeOf(endDate ?? startDate)}`
  }

  return formatClosureIntervalFmt(
    projectDateStore,
    {
      startDate,
      endDate: endDate ?? startDate,
      isDaily: resolveOperatingIntervalIsDaily(interval),
    },
    false,
  )
}

// Always the project's clock: an operating window or closure belongs to the site, so
// every viewer must read the same hours off it regardless of where they are.
export function formatClosureIntervalFmt(
  projectDateStore: ProjectDateStore,
  { startDate, endDate, isDaily }: ILocationClosureInterval,
  includeWeekday = true,
): string {
  const { isThisYear } = projectDateStore

  const timeOf = (date: number) => projectDateStore.getTimeToDisplay(date)

  const dateTimeOf = (date: number) => {
    if (includeWeekday) {
      return isThisYear(date)
        ? projectDateStore.getWeekdayMonthDayAndTimeToDisplay(date)
        : projectDateStore.getWeekdayMonthDayYearAndTimeToDisplay(date)
    }
    return isThisYear(date)
      ? projectDateStore.getMonthDayAndTimeToDisplay(date)
      : projectDateStore.getMonthDayYearAndTimeToDisplay(date)
  }

  if (isDaily) {
    return `${Localization.translator.daily}, ${timeOf(startDate)} – ${timeOf(
      endDate,
    )}`
  }

  const startDateFormatted = dateTimeOf(startDate)
  const endDateFormatted = endDate
    ? dateTimeOf(endDate)
    : Localization.translator.forTheFuture

  return `${startDateFormatted} - ${endDateFormatted}`
}
